/**
 * Kotlin method generator - Functional implementation with dependency injection
 * Generates Kotlin method implementations for BrickModule bridge
 */

import type { ModuleConstant, ModuleMethod, ParsedModule } from "../../parser";
import { isAnyObjectType, isAnyType } from "../../utils/type-helpers";
import type { KotlinTypeMapper } from "./kotlin-type-mapper";

export interface KotlinMethodGeneratorDeps {
  typeMapper: KotlinTypeMapper;
}

export interface KotlinMethodGenerator {
  /**
   * Generates a Kotlin method for BrickModule (override for New Arch)
   */
  generateKotlinMethod(method: ModuleMethod, module: ParsedModule): string;

  /**
   * Generates Kotlin implementation method with type conversion
   */
  generateKotlinImplMethod(method: ModuleMethod, module: ParsedModule): string;

  /**
   * Generates constant implementation getter using type-safe casting
   */
  generateConstantImplGetter(
    constant: ModuleConstant & { moduleName: string }
  ): string;
}

/**
 * Creates a Kotlin method generator with injected dependencies
 */
export function createKotlinMethodGenerator(
  deps: KotlinMethodGeneratorDeps
): KotlinMethodGenerator {
  const { typeMapper } = deps;

  /**
   * Helper function to generate the correct WritableMap put call for a value based on type
   */
  function generatePutValueCall(
    returnType: string,
    valueName: string = "value"
  ): string {
    // Handle Promise types
    if (returnType.startsWith("Promise<") && returnType.endsWith(">")) {
      const innerType = returnType.slice(8, -1);
      return generatePutValueCall(innerType, valueName);
    }

    // Determine the appropriate put method based on type
    switch (returnType) {
      case "string":
        return `putString("value", ${valueName})`;
      case "number":
        return `putDouble("value", ${valueName})`;
      case "boolean":
        return `putBoolean("value", ${valueName})`;
      case "void":
        return `putNull("value")`;
      case "any":
      case "Any":
        // Any type needs dynamic conversion based on runtime type
        return `convertAnyToMap(${valueName}, this)`;
      default:
        // Handle arrays - use putArray for proper array serialization
        if (returnType.endsWith("[]")) {
          return `putArray("value", ${valueName})`;
        }
        // For all other types (objects, AnyObject, custom interfaces) - use putMap
        // These are already converted to WritableMap in BrickModuleImpl
        return `putMap("value", ${valueName})`;
    }
  }

  /**
   * Generates a Kotlin method for BrickModule (override for New Arch)
   */
  function generateKotlinMethod(
    method: ModuleMethod,
    module: ParsedModule
  ): string {
    const methodName = `${module.moduleName}_${method.name}`;

    // Generate parameter list
    const params = method.params
      .map((param) => {
        // Prefer parser-provided isFunction over string heuristics
        const kotlinType = param.isFunction
          ? "Callback"
          : typeMapper.mapTypeScriptToReactNativeKotlin(
              param.type,
              false,
              param.optional
            );
        return `${param.name}: ${kotlinType}`;
      })
      .join(", ");

    const promiseParam = method.isAsync
      ? params
        ? ", promise: Promise"
        : "promise: Promise"
      : "";
    // Sync methods now return WritableMap for wrapped result
    const returnType = method.isAsync ? "Unit" : "WritableMap";

    // Generate call parameters
    const callParams = method.params.map((param) => param.name).join(", ");
    const asyncParam = method.isAsync
      ? callParams
        ? ", promise"
        : "promise"
      : "";

    const putValueCall = method.isAsync
      ? ""
      : generatePutValueCall(method.returnType);

    return `    override fun ${methodName}(${params}${promiseParam}): ${returnType} {
        ${
          method.isAsync
            ? `try {
            brickModuleImpl.${methodName}(${callParams}${asyncParam})
        } catch (e: Exception) {
            promise.reject("BRICK_ERROR", e.message, e)
        }`
            : `return try {
            val value = brickModuleImpl.${methodName}(${callParams}${asyncParam})
            Arguments.createMap().apply {
                putBoolean("~sync", true)
                putBoolean("success", true)
                ${putValueCall}
            }
        } catch (e: Exception) {
            Arguments.createMap().apply {
                putBoolean("~sync", true)
                putBoolean("success", false)
                putMap("error", Arguments.createMap().apply {
                    putString("code", "EXECUTION_ERROR")
                    putString("message", e.message ?: "Unknown error")
                })
            }
        }`
        }
    }`;
  }

  /**
   * Generates Kotlin implementation method with type conversion
   */
  function generateKotlinImplMethod(
    method: ModuleMethod,
    module: ParsedModule
  ): string {
    const methodName = `${module.moduleName}_${method.name}`;

    // Generate parameters with proper Kotlin types
    const params = method.params
      .map((param) => {
        const kotlinType = (param as { isFunction?: boolean }).isFunction
          ? "Callback"
          : typeMapper.mapTypeScriptToReactNativeKotlin(
              param.type,
              false,
              param.optional
            );
        return `${param.name}: ${kotlinType}`;
      })
      .join(", ");

    const promiseParam = method.isAsync
      ? params
        ? ", promise: Promise"
        : "promise: Promise"
      : "";
    const returnType = method.isAsync
      ? "Unit"
      : typeMapper.mapTypeScriptToReactNativeKotlin(method.returnType, true);

    // Generate implementation
    const implementation = generateMethodImplementation(method, module);

    return `    fun ${methodName}(${params}${promiseParam}): ${returnType} {
        try {
            ${implementation}
        } catch (e: Exception) {
            ${
              method.isAsync
                ? `promise.reject("BRICK_ERROR", e.message, e)`
                : `throw e`
            }
        }
    }`;
  }

  /**
   * Generates constant implementation getter using type-safe casting
   */
  function generateConstantImplGetter(
    constant: ModuleConstant & { moduleName: string }
  ): string {
    const interfaceName = `${constant.moduleName}Spec`;
    return `    fun ${constant.moduleName}_${
      constant.name
    }(): ${typeMapper.mapTypeScriptToKotlin(constant.type)} {
        try {
            val activity = reactContext.currentActivity
                ?: return ${typeMapper.getDefaultKotlinReturnValue(
                  constant.type
                )}

            if (activity !is BrickModuleRegistrar) {
                return ${typeMapper.getDefaultKotlinReturnValue(constant.type)}
            }

            val module = activity.getModuleRegistry().getModuleInstance("${
              constant.moduleName
            }")
                ?: return ${typeMapper.getDefaultKotlinReturnValue(
                  constant.type
                )}

            val typedModule = module as? ${interfaceName}
                ?: return ${typeMapper.getDefaultKotlinReturnValue(
                  constant.type
                )}

            return typedModule.${constant.name}
        } catch (e: Exception) {
            return ${typeMapper.getDefaultKotlinReturnValue(constant.type)}
        }
    }`;
  }

  /**
   * Generates the method implementation including conversions
   */
  function generateMethodImplementation(
    method: ModuleMethod,
    module: ParsedModule
  ): string {
    const paramConversions = generateParameterConversions(method, module);
    const interfaceName = `${module.moduleName}Spec`;

    // Generate parameter list for method call
    const methodParams = method.params
      .map((param) => {
        const bridgeType = typeMapper.mapTypeScriptToReactNativeKotlin(
          param.type,
          false,
          param.optional
        );
        // Wrap Callback into Kotlin lambda matching Spec signature
        if (param.isFunction) {
          const argCount = param.functionType?.paramTypes?.length || 0;
          if (argCount === 0) {
            return `{ ${param.name}.invoke() }`;
          }
          const argNames = Array.from({ length: argCount }, (_, i) => `v${i}`);
          // Special case: a single rest parameter (e.g., ...result: AnyObject[])
          const isSingleRestArray =
            argCount === 1 && param.functionType?.paramRestFlags?.[0] === true;
          if (isSingleRestArray) {
            const t0 = (param.functionType?.paramTypes?.[0] || "").trim();
            const elem0 = t0.endsWith("[]") ? t0.slice(0, -2).trim() : t0;
            // any[] is modeled as Array<Any?> in Spec -> spread directly
            if (elem0 === "any") {
              return `{ v0 -> ${param.name}.invoke(*v0) }`;
            }
            // AnyObject[] (or {})[] is modeled as List<WritableMap> -> convert to Array then spread
            if (isAnyObjectType(elem0)) {
              return `{ v0 -> ${param.name}.invoke(*v0.toTypedArray()) }`;
            }
            // Other array types (e.g., number[]) are modeled as WritableArray -> pass as single arg
            return `{ v0 -> ${param.name}.invoke(v0) }`;
          }
          const convertArg = (t: string, idx: number) => {
            const tt = (t || "").trim();
            // Primitive values pass through
            if (tt === "string" || tt === "number" || tt === "boolean") {
              return `v${idx}`;
            }
            // Arrays (of primitives or complex) -> convert to WritableArray for RN Callback
            if (tt.endsWith("[]")) {
              return `convertToWritableArray(v${idx})`;
            }
            // AnyObject/{} should map to RN map type; pass through as provided by typed module
            if (isAnyObjectType(tt)) {
              return `v${idx}`;
            }
            // RN bridge types pass through as-is
            if (
              tt === "WritableMap" ||
              tt === "ReadableMap" ||
              tt === "WritableArray" ||
              tt === "ReadableArray"
            ) {
              return `v${idx}`;
            }
            // Typed interface (PascalCase and not RN types) -> convert to WritableMap for Callback
            if (typeMapper.isPascalCase(tt)) {
              return `convertToWritableMap(v${idx})`;
            }
            // Fallback: pass through
            return `v${idx}`;
          };
          const convertedArgs = (param.functionType?.paramTypes || []).map(
            (t: string, idx: number) => convertArg(t, idx)
          );
          return `{ ${argNames.join(", ")} -> ${
            param.name
          }.invoke(${convertedArgs.join(", ")}) }`;
        }
        if (
          bridgeType === "ReadableMap?" ||
          bridgeType === "ReadableArray?" ||
          bridgeType === "ReadableMap" ||
          bridgeType === "ReadableArray"
        ) {
          // For '{}' or 'AnyObject' or 'Any', pass through directly without conversion
          const tt = (param.type || "").trim();
          if (isAnyObjectType(tt) || isAnyType(tt)) {
            return param.name;
          }
          return `${param.name}Converted`;
        }
        return param.name;
      })
      .join(", ");

    const baseImplementation = `// Get the activity and module registry
        val activity = reactContext.currentActivity
            ?: throw BrickModuleError.ModuleNotFound("No current activity")

        if (activity !is BrickModuleRegistrar) {
            throw BrickModuleError.ModuleNotFound("Current activity does not implement BrickModuleRegistrar")
        }

        val module = activity.getModuleRegistry().getModuleInstance("${
          module.moduleName
        }")
            ?: throw BrickModuleError.ModuleNotFound("Module '${
              module.moduleName
            }' not found")

        // Cast to the expected interface
        val typedModule = module as? ${interfaceName}
            ?: throw BrickModuleError.TypeMismatch("Module does not implement ${interfaceName}")

        ${paramConversions ? `${paramConversions}\n        ` : ""}`;

    if (method.isAsync) {
      // Handle return type conversion for async methods
      let returnConversion = "promise.resolve(result)";
      let actualReturnType = method.returnType;
      if (
        actualReturnType.startsWith("Promise<") &&
        actualReturnType.endsWith(">")
      ) {
        actualReturnType = actualReturnType.slice(8, -1);
      }

      // SPECIAL CASE: Handle Promise<void> - resolve with null instead of Unit
      if (actualReturnType === "void") {
        returnConversion = "promise.resolve(null)";
      } else if (isAnyType(actualReturnType)) {
        // Any can be any value type, needs dynamic conversion
        returnConversion = "promise.resolve(convertAnyValue(result))";
      } else if (isAnyObjectType(actualReturnType)) {
        // AnyObject maps to WritableMap, no conversion needed
        returnConversion = "promise.resolve(result)";
      } else if (
        actualReturnType.startsWith("{") &&
        actualReturnType.endsWith("}")
      ) {
        returnConversion = "promise.resolve(convertToWritableMap(result))";
      } else if (actualReturnType.endsWith("[]")) {
        returnConversion = "promise.resolve(convertToWritableArray(result))";
      } else if (!typeMapper.isPrimitiveType(actualReturnType)) {
        // Handle complex return types (interfaces like UserResult) that need conversion to WritableMap
        returnConversion = "promise.resolve(convertToWritableMap(result))";
      }

      return `${baseImplementation}scope.launch {
        try {
            val result = typedModule.${method.name}(${methodParams})
            ${returnConversion}
        } catch (e: Exception) {
            promise.reject("BRICK_ERROR", e.message, e)
        }
    }`;
    } else {
      // Handle return type conversion for sync methods
      let returnStatement = `return typedModule.${method.name}(${methodParams})`;

      if (isAnyType(method.returnType)) {
        // Any can be any value type, no conversion needed
        returnStatement = `return typedModule.${method.name}(${methodParams})`;
      } else if (isAnyObjectType(method.returnType)) {
        // AnyObject maps to WritableMap, no conversion needed
        returnStatement = `return typedModule.${method.name}(${methodParams})`;
      } else if (
        method.returnType.startsWith("{") &&
        method.returnType.endsWith("}")
      ) {
        returnStatement = `val result = typedModule.${method.name}(${methodParams})
            return convertToWritableMap(result)`;
      } else if (method.returnType.endsWith("[]")) {
        returnStatement = `val result = typedModule.${method.name}(${methodParams})
            return convertToWritableArray(result)`;
      } else if (
        method.returnType !== "string" &&
        method.returnType !== "number" &&
        method.returnType !== "boolean" &&
        method.returnType !== "void" &&
        !method.returnType.includes("Promise")
      ) {
        // Handle complex return types that need conversion to WritableMap
        returnStatement = `val result = typedModule.${method.name}(${methodParams})
            return convertToWritableMap(result)`;
      }

      return `${baseImplementation}${returnStatement}`;
    }
  }

  /**
   * Generates parameter conversions for Kotlin methods with concrete types
   */
  function generateParameterConversions(
    method: ModuleMethod,
    module: ParsedModule
  ): string {
    const conversions: string[] = [];

    for (const param of method.params) {
      // Skip conversion for function/callback parameters; handled separately as lambdas
      if (param.isFunction) {
        continue;
      }
      // Get the React Native bridge type for this parameter
      const bridgeType = typeMapper.mapTypeScriptToReactNativeKotlin(
        param.type,
        false,
        param.optional
      );

      if (bridgeType === "ReadableMap" || bridgeType === "ReadableMap?") {
        // For '{}' or 'AnyObject' or 'Any', typed module expects ReadableMap directly; skip conversion
        const t = (param.type || "").trim();
        if (isAnyObjectType(t) || isAnyType(t)) {
          continue;
        }
        // Generate concrete type class name from TypeScript type
        const className = generateConcreteTypeName(
          module.moduleName,
          method.name,
          param.name,
          param.type
        );
        // Use Gson with concrete type for better type safety
        conversions.push(
          `val ${param.name}Converted = convertFromReadableMap(${param.name}, ${className}::class.java)`
        );
      } else if (
        bridgeType === "ReadableArray" ||
        bridgeType === "ReadableArray?"
      ) {
        // Array conversion based on element type
        const elementType = param.type.slice(0, -2);
        if (elementType === "string") {
          conversions.push(
            `val ${param.name}Converted = convertStringArrayFromReadableArray(${param.name})`
          );
        } else if (elementType === "number") {
          conversions.push(
            `val ${param.name}Converted = convertDoubleArrayFromReadableArray(${param.name})`
          );
        } else {
          // For complex array elements, generate element type class
          const elementClassName = generateConcreteTypeName(
            module.moduleName,
            method.name,
            `${param.name}Element`,
            elementType
          );
          conversions.push(
            `val ${param.name}Converted = convertFromReadableArray(${param.name}, ${elementClassName}::class.java)`
          );
        }
      }
    }

    return conversions.length > 0 ? conversions.join("\n        ") : "";
  }

  /**
   * Generates concrete type name for TypeScript type
   */
  function generateConcreteTypeName(
    moduleName: string,
    methodName: string,
    paramName: string,
    tsType: string
  ): string {
    // If it's a known interface name (like User, UserResult), use it directly
    if (
      !tsType.startsWith("{") &&
      !tsType.endsWith("}") &&
      !tsType.endsWith("[]")
    ) {
      return tsType;
    }

    // For inline object types, generate a class name
    const capitalizedMethodName =
      tsType.charAt(0).toUpperCase() + methodName.slice(1);
    const capitalizedParamName =
      paramName.charAt(0).toUpperCase() + paramName.slice(1);
    return `${moduleName}${capitalizedMethodName}${capitalizedParamName}`;
  }

  return {
    generateKotlinMethod,
    generateKotlinImplMethod,
    generateConstantImplGetter,
  };
}
